<template>
  <section>
    <div class="listview__head">
      <div>
        <div class="c-lbl">Facturación mensual</div>
        <div class="listview__desc">Generar lote de facturas para clientes con facturación activa.</div>
      </div>
    </div>

    <div class="card card-p mb-16">
      <div class="c-lbl mb-8">Configuración del lote</div>
      <div class="form-row form-row--3">
        <div>
          <label class="fl">Período a facturar</label>
          <input class="fi" type="month" v-model="periodo" />
        </div>
        <div>
          <label class="fl">Fecha de cotización BNA</label>
          <input class="fi" type="date" v-model="fechaCotizacion" />
        </div>
        <div>
          <label class="fl">Fecha de emisión</label>
          <input class="fi" type="date" v-model="fechaEmision" />
        </div>
      </div>
      <div class="form-row form-row--2 mt-8" v-if="cotizacion">
        <div class="fi fi--ro">Dólar Compra: <strong>{{ fmtNum(cotizacion.compra) }}</strong> <span class="small">(BNA {{ fechaCotizacion }})</span></div>
        <div class="fi fi--ro">Dólar Venta: <strong>{{ fmtNum(cotizacion.venta) }}</strong> <span class="small">(BNA {{ fechaCotizacion }})</span></div>
      </div>
      <div v-if="bnaError" class="form-row form-row--2 mt-8">
        <div>
          <label class="fl">Dólar Compra (manual)</label>
          <input class="fi" v-model.number="manualCompra" type="number" step="0.01" min="0" />
        </div>
        <div>
          <label class="fl">Dólar Venta (manual)</label>
          <input class="fi" v-model.number="manualVenta" type="number" step="0.01" min="0" />
        </div>
      </div>
      <div v-if="bnaError" style="margin-top:6px">
        <button class="btn btn-g btn-sm" @click="aplicarManual" :disabled="!manualCompra || !manualVenta">Aplicar cotización manual</button>
      </div>
      <div style="margin-top:12px;display:flex;gap:8px">
        <button class="btn btn-g" @click="consultarBNA" :disabled="busy">
          {{ busy ? 'Consultando...' : 'Consultar BNA' }}
        </button>
        <button class="btn btn-p" @click="generarLote" :disabled="busy">
          Generar lote
        </button>
      </div>
      <div v-if="error" class="form-error" style="margin-top:10px">{{ error }}</div>
      <div v-if="info" style="margin-top:10px;padding:10px 14px;background:#e8f5e9;color:#2e7d32;border-radius:6px;font-size:13px;">{{ info }}</div>
    </div>

    <!-- Listado facturas -->
    <div v-if="facturas.length" class="fe-list">
      <div v-for="f in facturas" :key="f.id" class="fe-card" :class="{ 'fe-card--ok': f.cae }">
        <div class="fe-card__top">
          <div class="fe-card__cliente">{{ f.cliente?.nombre || clienteNombre(f.cliente_id) }}</div>
          <span class="pill" :class="f.cae ? 'pill-g' : 'pill-b'">{{ f.cae ? 'Autorizada' : 'Pendiente' }}</span>
        </div>
        <div class="fe-card__row">
          <span class="fe-card__lbl">Emisor</span>
          <span>{{ emisorNombre(f.cuit_emisor) }}</span>
        </div>
        <div v-if="f.nota_pdf" class="fe-card__row" :title="f.nota_pdf">
          <span class="fe-card__lbl">Nota</span>
          <span><span class="pill pill-b">NOTA</span> {{ f.nota_pdf.length > 70 ? f.nota_pdf.slice(0, 70) + '…' : f.nota_pdf }}</span>
        </div>
        <div class="fe-card__nums">
          <div><span class="fe-card__lbl">USD</span><span class="mono">{{ fmtNum(f.monto_usd) }}</span></div>
          <div><span class="fe-card__lbl">TC</span><span class="mono">{{ fmtNum(f.cotizacion_usada) }}</span></div>
          <div><span class="fe-card__lbl">ARS</span><span class="mono bold">{{ fmtNum(f.monto_total) }}</span></div>
        </div>
        <div v-if="f.cae" class="fe-card__cae">
          <span class="fe-card__lbl">CAE</span>
          <span class="mono">{{ f.cae }}</span>
        </div>
        <div class="fe-card__actions">
          <button v-if="!f.cae" class="btn-ico" @click="openEdit(f)" title="Editar">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
          <button v-if="!f.cae" class="btn btn-g btn-sm" @click="autorizarUna(f)" :disabled="f._busy">Autorizar</button>
          <a v-if="f.cae" class="btn btn-g btn-sm" :href="pdfUrl(f.id)" target="_blank">PDF</a>
        </div>
      </div>
      <div style="padding:12px 0;display:flex;gap:8px;justify-content:flex-end">
        <button v-if="facturasPendientes.length" class="btn btn-p" @click="autorizarTodas" :disabled="busy">
          {{ busy ? 'Autorizando...' : `Autorizar todas (${facturasPendientes.length})` }}
        </button>
        <button v-if="facturasAutorizadas.length && !facturasPendientes.length" class="btn btn-p" @click="enviarTodosEmails" :disabled="busy">
          {{ busy ? 'Enviando...' : `Enviar emails (${facturasAutorizadas.length})` }}
        </button>
      </div>
    </div>

    <div v-if="mensajes.length" class="card card-p mb-16" style="margin-top:12px">
      <div v-for="(m, i) in mensajes" :key="i" :class="m.ok ? 'form-ok' : 'form-error'" style="margin-bottom:4px">
        {{ m.text }}
      </div>
    </div>

    <!-- Modal editar factura antes de autorizar -->
    <Modal v-model="edit.open" title="Editar factura" :max-width="480">
      <form @submit.prevent="saveEdit" class="form-grid" id="edit-fact-form">
        <div>
          <label class="fl">Descripción</label>
          <input class="fi" v-model="edit.descripcion" maxlength="255" />
        </div>
        <div class="form-row form-row--2">
          <div>
            <label class="fl">Monto USD</label>
            <input class="fi" v-model.number="edit.monto_usd" type="number" step="0.01" min="0" @input="onEditUsdChange" />
          </div>
          <div>
            <label class="fl">Monto ARS (total)</label>
            <input class="fi" v-model.number="edit.monto_total" type="number" step="0.01" min="0" @input="onEditArsChange" />
          </div>
        </div>
        <div v-if="edit.tc" class="small" style="color:var(--tx-md);margin-top:-4px">
          Cotización: {{ fmtNum(edit.tc) }} ({{ edit.tcTipo }}) — BNA {{ fechaCotizacion }}
        </div>
        <div>
          <label class="fl">Nota impresa en la factura (gastos a recuperar)</label>
          <textarea class="fi" v-model="edit.nota_pdf" rows="3" maxlength="600"></textarea>
          <div v-if="edit.nota_pdf" style="display:flex;justify-content:flex-end;margin-top:4px">
            <button type="button" class="btn btn-g btn-sm" style="color:#c33" @click="edit.nota_pdf = ''">Quitar nota de esta factura</button>
          </div>
        </div>
        <div v-if="edit.error" class="form-error">{{ edit.error }}</div>
      </form>
      <template #footer>
        <button class="btn btn-g" @click="edit.open = false">Cancelar</button>
        <button class="btn btn-p" form="edit-fact-form" type="submit" :disabled="edit.saving">
          {{ edit.saving ? 'Guardando...' : 'Guardar' }}
        </button>
      </template>
    </Modal>
  </section>
</template>

<script>
import api from '../services/api';
import Modal from '../components/Modal.vue';

const API_BASE = process.env.VUE_APP_API_URL || 'http://localhost:8000/api';

export default {
  name: 'FacturacionMensualView',
  components: { Modal },
  data() {
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return {
      periodo: `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, '0')}`,
      fechaCotizacion: '',
      fechaEmision: now.toLocaleDateString('en-CA'),
      cotizacion: null,
      facturas: [],
      emisores: [],
      clientes: [],
      error: '',
      info: '',
      busy: false,
      mensajes: [],
      bnaError: false,
      manualCompra: null,
      manualVenta: null,
      edit: { open: false, id: null, descripcion: '', monto_usd: 0, monto_total: 0, nota_pdf: '', saving: false, error: '' }
    };
  },
  computed: {
    facturasPendientes() { return this.facturas.filter(f => !f.cae); },
    facturasAutorizadas() { return this.facturas.filter(f => f.cae); },
    token() {
      try { return JSON.parse(localStorage.getItem('mhlm.session'))?.token; } catch { return ''; }
    }
  },
  watch: {
    periodo: {
      immediate: true,
      handler(val) {
        if (!val) return;
        const [y, m] = val.split('-').map(Number);
        const mesSig = new Date(y, m, 1);
        while (mesSig.getDay() === 0 || mesSig.getDay() === 6) mesSig.setDate(mesSig.getDate() + 1);
        this.fechaCotizacion = mesSig.toLocaleDateString('en-CA');
        this.cotizacion = null;
        this.facturas = [];
        this.mensajes = [];
        this.error = '';
      }
    }
  },
  async mounted() {
    await this.loadEmisores();
  },
  methods: {
    fmtNum(v) { return v != null ? Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'; },
    async loadEmisores() {
      try {
        const { data } = await api.get('/emisores');
        this.emisores = data.data || [];
      } catch (e) { /* ignore */ }
    },
    emisorNombre(cuit) {
      const e = this.emisores.find(em => em.cuit === cuit);
      return e ? e.razon_social.split(' ').slice(0, 2).join(' ') : cuit || '—';
    },
    clienteNombre(id) {
      const c = this.clientes.find(cl => cl.id === id);
      return c ? c.nombre : `#${id}`;
    },
    async consultarBNA() {
      this.busy = true; this.error = ''; this.bnaError = false;
      try {
        const { data } = await api.get('/fe/cotizacion', { params: { fecha: this.fechaCotizacion } });
        this.cotizacion = data;
      } catch (e) {
        this.bnaError = true;
        // El backend explica la causa (fecha futura, feriado/fin de semana, o sitio caído).
        this.error = e?.response?.data?.message || 'No se pudo consultar el BNA. Podés ingresar la cotización manualmente.';
      } finally { this.busy = false; }
    },
    aplicarManual() {
      this.cotizacion = { compra: this.manualCompra, venta: this.manualVenta, fecha: this.fechaCotizacion };
      this.bnaError = false;
      this.error = '';
    },
    async generarLote() {
      if (!this.cotizacion) {
        this.error = 'Consultá la cotización BNA antes de generar el lote.';
        return;
      }
      this.busy = true; this.error = ''; this.info = ''; this.mensajes = [];
      try {
        const { data } = await api.post('/fe/generar-lote-mensual', {
          periodo: this.periodo,
          fecha_emision: this.fechaEmision,
          fecha_cotizacion: this.fechaCotizacion,
          // Si la cotización se cargó a mano, el backend la usa en vez de volver a consultar BNA.
          cotizacion_compra: this.cotizacion?.compra || null,
          cotizacion_venta:  this.cotizacion?.venta  || null,
        });
        const nuevas = (data.facturas || []).map(f => ({ ...f, _busy: false }));
        if (nuevas.length) {
          this.facturas = nuevas;
          this.info = `${data.generadas} factura(s) generada(s). Cotización compra: ${this.fmtNum(data.cotizacion?.compra)} / venta: ${this.fmtNum(data.cotizacion?.venta)}`;
          // Avisos del pendiente de gastos (feed de mhlm.ar/gastos no disponible o copia vieja).
          (data.avisos || []).forEach(a => this.mensajes.push({ ok: false, text: `Gastos a recuperar — ${a}` }));
        } else {
          await this.cargarExistentes();
          if (this.facturas.length) {
            this.info = `${this.facturas.length} factura(s) pendiente(s) de autorización para este período.`;
          } else {
            this.info = `El período ${this.periodo} ya está completamente facturado y autorizado.`;
          }
        }
      } catch (e) {
        console.error('generarLote error:', e);
        this.error = e?.response?.data?.message || 'Error al generar lote: ' + (e?.message || 'desconocido');
      } finally { this.busy = false; }
    },
    async cargarExistentes() {
      try {
        const { data } = await api.get('/facturas', { params: { periodo: this.periodo, per_page: 50 } });
        const items = data?.data?.data || data?.data || [];
        this.facturas = items.filter(f => !f.cae || f.cae.startsWith('92828')).map(f => ({ ...f, _busy: false }));
        if (this.facturas.length) {
          this.mensajes.push({ ok: true, text: `${this.facturas.length} factura(s) existente(s) cargada(s) para el período.` });
        }
      } catch (e) { /* ignore */ }
    },
    async autorizarUna(f) {
      f._busy = true;
      try {
        const { data } = await api.post(`/facturas/${f.id}/autorizar`);
        Object.assign(f, data.factura || {});
      } catch (e) {
        this.mensajes.push({ ok: false, text: `Error ${f.id}: ${e?.response?.data?.message || e.message}` });
      } finally { f._busy = false; }
    },
    async autorizarTodas() {
      this.busy = true; this.mensajes = [];
      const ids = this.facturasPendientes.map(f => f.id);
      try {
        const { data } = await api.post('/fe/autorizar-lote', { factura_ids: ids });
        for (const r of (data.resultados || [])) {
          const f = this.facturas.find(ff => ff.id === r.id);
          if (r.ok && f) {
            f.cae = r.cae;
            f.resultado_arca = 'A';
          }
          if (!r.ok) this.mensajes.push({
            ok: false,
            text: `#${r.id} error: ${r.error}`
          });
        }
      } catch (e) {
        this.error = e?.response?.data?.message || 'Error al autorizar lote.';
      } finally { this.busy = false; }
    },
    async enviarTodosEmails() {
      const autorizadas = this.facturasAutorizadas;
      if (!autorizadas.length) return;
      const ok = await this.$msg.confirm(`Enviar ${autorizadas.length} email(s) de facturación?`, 'Enviar emails');
      if (!ok) return;
      this.busy = true; this.error = ''; this.info = '';
      let enviados = 0, errores = [];
      for (const f of autorizadas) {
        this.info = `Enviando ${enviados + 1} de ${autorizadas.length}...`;
        try {
          await api.post(`/facturas/${f.id}/enviar-email`);
          enviados++;
        } catch (e) {
          errores.push(`${f.cliente?.nombre || f.id}: ${e?.response?.data?.message || 'error'}`);
        }
      }
      this.info = `${enviados} email(s) enviado(s)` + (errores.length ? `, ${errores.length} error(es)` : '');
      if (errores.length) this.error = errores.join(' | ');
      this.busy = false;
    },
    openEdit(f) {
      const tc = Number(f.cotizacion_usada) || (this.cotizacion?.compra || 0);
      const tcTipo = f.cotizacion_usada == this.cotizacion?.venta ? 'venta' : 'compra';
      this.edit = {
        open: true, id: f.id,
        descripcion: f.descripcion || '',
        monto_usd: Number(f.monto_usd) || 0,
        monto_total: Number(f.monto_total) || 0,
        nota_pdf: f.nota_pdf || '',
        tc, tcTipo,
        saving: false, error: ''
      };
    },
    onEditUsdChange() {
      if (this.edit.tc && this.edit.monto_usd) {
        this.edit.monto_total = Math.round(this.edit.monto_usd * this.edit.tc * 100) / 100;
      }
    },
    onEditArsChange() {
      if (this.edit.tc && this.edit.monto_total) {
        this.edit.monto_usd = Math.round(this.edit.monto_total / this.edit.tc * 100) / 100;
      }
    },
    async saveEdit() {
      this.edit.saving = true; this.edit.error = '';
      if (!this.edit.monto_total || this.edit.monto_total <= 0) {
        this.edit.error = 'El importe ARS no puede ser 0 o vacío.';
        this.edit.saving = false;
        return;
      }
      try {
        await api.put(`/facturas/${this.edit.id}`, {
          descripcion: this.edit.descripcion,
          monto_usd: this.edit.monto_usd,
          monto_total: this.edit.monto_total,
          imp_neto: this.edit.monto_total,
          nota_pdf: this.edit.nota_pdf || null,
        });
        const f = this.facturas.find(ff => ff.id === this.edit.id);
        if (f) {
          f.descripcion = this.edit.descripcion;
          f.monto_usd = this.edit.monto_usd;
          f.monto_total = this.edit.monto_total;
          f.nota_pdf = this.edit.nota_pdf || null;
        }
        this.edit.open = false;
      } catch (e) {
        this.edit.error = e?.response?.data?.message || 'No se pudo guardar.';
      } finally { this.edit.saving = false; }
    },
    pdfUrl(id) {
      return `${API_BASE}/facturas/${id}/pdf?token=${this.token}`;
    }
  }
};
</script>

<style scoped>
.fi.fi--ro{ background:var(--acc-xlt); color:var(--tx-hi); cursor:default; }
.form-ok{ color:#2a7a3a; font-size:12.5px; background:#e5f6e9; padding:8px 12px; border-radius:6px; }
.mt-8{ margin-top:8px; }
.small{ font-size:11.5px; color:var(--tx-md); }
.bold{ font-weight:700; }
.mono{ font-family:'SF Mono','Consolas',monospace; font-size:12px; }

/* Tarjetas facturación */
.fe-list{ display:flex; flex-direction:column; gap:8px; margin-top:12px; }
.fe-card{
  background:var(--surface); border:1px solid var(--border);
  border-radius:10px; padding:12px 16px;
  display:flex; flex-direction:column; gap:6px;
}
.fe-card--ok{ border-left:3px solid #2E9E67; }
.fe-card__top{ display:flex; justify-content:space-between; align-items:center; gap:8px; }
.fe-card__cliente{ font-weight:700; color:var(--tx-hi); font-size:13px; }
.fe-card__row{ display:flex; justify-content:space-between; font-size:12px; color:var(--tx-md); }
.fe-card__nums{
  display:grid; grid-template-columns:1fr 1fr 1fr; gap:8px;
  padding:6px 0; border-top:1px solid var(--border); border-bottom:1px solid var(--border);
}
.fe-card__nums > div{ display:flex; flex-direction:column; gap:2px; }
.fe-card__lbl{ font-size:10px; text-transform:uppercase; letter-spacing:.05em; color:var(--tx-lo); font-weight:600; }
.fe-card__cae{ font-size:11px; display:flex; gap:6px; align-items:center; }
.fe-card__actions{ display:flex; gap:6px; justify-content:flex-end; align-items:center; }
</style>
